import { MouseEvent, useMemo, useState } from 'react'

import { Heading } from '@hhgtech/hhg-components'
import { Button, Modal, Text } from '@hhgtech/hhg-components/mantine'
import { Box, BoxProps, Flex, Rating, Stack, rem } from '@mantine/core'
import { useIntl } from 'react-intl'

import { domainLocales } from 'domainLocales'
import { useRouterExtend } from 'hooks/useRouterExtend'
import {
  CARE_LOCALISED_SLUGS,
  MANTINE_MODAL_TARGET,
} from 'interfaces/constants'

import { DoctorAvatar, DoctorAvatarProps } from './doctorAvatar'
import useStyles from './index.styles'
import { Info } from './info'
import { PromotionTag } from './promotionTag'
import { Tags } from './tags'
import { getHelloSitesUrl } from 'api/utils/paths'

export type DoctorCardDisplay = 'wide-horizon' | 'horizon' | 'vertical'
export type LayoutConfig = { [key in number]: DoctorCardDisplay }

export type CardItem = {
  name: string
  validOutpatient: boolean
  validTelemedicine: boolean
  rating: string
  doctorSpecialties: string[]
  hospital: string
  slug: string
  id: string
  avatar: string
  price: string
  priceUnit: string
  originalPrice: string
  cityName: string
}

export type DoctorCardProps = {
  item: CardItem
  display?: DoctorCardDisplay
  isService?: boolean
  hidePrice?: boolean
  showConfirmCityPopup?: boolean
  showPromotionEvent?: boolean
  campaign?: {
    name: string
    source: string
  }
  onCancel?: () => void
} & BoxProps

const DoctorCard = ({
  item,
  className,
  classNames,
  display = 'vertical',
  styles,
  isService,
  hidePrice,
  showConfirmCityPopup = false,
  onCancel,
  showPromotionEvent,
  campaign = {
    name: '',
    source: '',
  },
  ...boxProps
}: DoctorCardProps) => {
  const {
    validOutpatient,
    validTelemedicine,
    rating,
    slug,
    id,
    cityName,
  } = item
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()
  const { classes, cx } = useStyles(
    // First argument of useStyles is styles params
    { display },
    // Second argument is responsible for styles api integration
    { name: 'DoctorCard', classNames, styles },
  )
  const formatedRating = Math.floor(Number(rating))
  const [showModal, setShowModal] = useState<{
    url: string
    cityName: string
  } | null>(null)

  const avatarProps: DoctorAvatarProps = {
    doctor: {
      ...item,
      ...(display === 'wide-horizon' && {
        validOutpatient: false,
        validTelemedicine: false,
      }),
    },
    ratio: display === 'vertical' || display === 'wide-horizon' ? 264 / 176 : 1,
    className: classes.avatar,
  }

  const ratingEle =
    formatedRating > 0 ? (
      <Flex gap={rem(8)} align={'center'}>
        <Rating defaultValue={formatedRating} readOnly />
        <Text size="s4" weight="semiBold">
          {parseFloat(rating)}/5
        </Text>
      </Flex>
    ) : null

  const url = useMemo(() => {
    const fullUrl = `${getHelloSitesUrl(locale)}/care/${
      CARE_LOCALISED_SLUGS[locale]?.[isService ? 'SERVICE' : 'DOCTOR']
    }/${slug}-${id}/`
    const newUrl = new URL(fullUrl)
    campaign.name && newUrl.searchParams.append('utm_campaign', campaign.name)
    campaign.source && newUrl.searchParams.append('utm_source', campaign.source)
    return newUrl.toString()
  }, [])

  const dataTrackingProps = {
    'data-event-category': 'Service Connection',
    'data-event-action': 'Book Doctor',
    'data-event-label': `https://${domainLocales[locale]}${url}`,
  }

  const handleClick = (
    e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    if (showConfirmCityPopup) {
      e.preventDefault()
      setShowModal({
        cityName,
        url,
      })
    } else {
      window.open(url)
    }
  }

  const commonLinkProps = {
    href: url,
    target: '_blank',
    style: { display: 'block', borderRadius: 'inherit' },
    className: 'pointer-event-child-none',
    onClick: handleClick,
    ...dataTrackingProps,
  }

  const btnEle = (
    <Button
      size={display === 'vertical' ? 'md' : 'sm'}
      className={classes.btn}
      onClick={handleClick}
      {...dataTrackingProps}
    >
      {isService
        ? f({ id: 'articlePage.careEmbedding.bookService' })
        : f({ id: 'articleAuthor.button', defaultMessage: 'Book now' })}
    </Button>
  )

  const promotionTags = (
    <>
      <PromotionTag
        color="yellow"
        textColor="yellow.9"
        hasTooltip
        label={
          <>
            {f({
              id: 'doctorCard.promotionTag.canUseVoucher',
              defaultMessage: 'Có thể áp dụng voucher',
            })}
          </>
        }
      />
      <PromotionTag
        color="#FDF1F6"
        textColor="#A50064"
        label={
          <>
            {f(
              {
                id: 'doctorCard.promotionTag.discount',
                defaultMessage: 'Thẻ quà tặng giảm thêm {price}',
              },
              {
                price: <strong>100.000 đ</strong>,
              },
            )}
          </>
        }
      />
      <PromotionTag
        color="#FDF1F6"
        textColor="#A50064"
        label={
          <>
            {f(
              {
                id: 'doctorCard.promotionTag.discount1',
                defaultMessage: 'Cơ hội nhận quà trị giá {price}',
              },
              {
                price: <strong>300.000 đ</strong>,
              },
            )}
          </>
        }
      />
    </>
  )

  return (
    <>
      <Box className={cx(classes.root, className)} {...boxProps}>
        {display === 'vertical' ? (
          <>
            <Box sx={{ borderRadius: 'inherit' }}>
              <a {...commonLinkProps}>
                <DoctorAvatar {...avatarProps} />
              </a>
              <Info
                className={classes.info}
                item={item}
                hidePrice={hidePrice}
                hrefProps={commonLinkProps}
              />
            </Box>
            <Stack
              spacing={rem(16)}
              sx={{ padding: `0 ${rem(16)} ${rem(12)}` }}
            >
              {showPromotionEvent && (
                <Stack
                  spacing={rem(4)}
                  align="flex-start"
                  sx={(theme) => ({
                    [theme.fn.smallerThan('sm')]: {
                      alignItems: 'stretch',
                    },
                  })}
                >
                  {promotionTags}
                </Stack>
              )}
              {btnEle}
            </Stack>
          </>
        ) : display === 'wide-horizon' ? (
          <>
            <a
              {...commonLinkProps}
              style={{ display: 'block', borderRadius: 'inherit' }}
            >
              <DoctorAvatar {...avatarProps} sx={{ width: rem(360) }} />
            </a>
            <Stack
              justify="space-between"
              sx={{ padding: rem(24), width: `calc(100% - ${rem(360)})` }}
            >
              <Flex align={'flex-start'} justify={'space-between'}>
                <Info
                  hideRating
                  hidePrice={hidePrice}
                  item={item}
                  hrefProps={commonLinkProps}
                />
                {ratingEle}
              </Flex>
              <Flex
                justify={'space-between'}
                sx={() => ({
                  position: 'relative',
                  paddingTop: rem(24),
                })}
              >
                <Tags
                  hasOutpatient={validOutpatient}
                  hasTelemedicine={validTelemedicine}
                />
                {btnEle}
                {showPromotionEvent && (
                  <Stack
                    align="flex-end"
                    spacing={rem(8)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      bottom: 0,
                    }}
                  >
                    {promotionTags}
                  </Stack>
                )}
              </Flex>
            </Stack>
          </>
        ) : (
          <>
            <a
              {...commonLinkProps}
              style={{ display: 'block', borderRadius: 'inherit' }}
            >
              <DoctorAvatar {...avatarProps} />
            </a>
            <Stack justify="space-between" sx={{ padding: rem(24) }}>
              <Info item={item} hidePrice={hidePrice} />
              <Box sx={{ width: '100%' }}>{btnEle}</Box>
            </Stack>
          </>
        )}
      </Box>
      {showConfirmCityPopup && (
        <Modal
          size={rem(408)}
          target={MANTINE_MODAL_TARGET}
          opened={Boolean(showModal)}
          withCloseButton
          styles={(theme) => ({
            header: {
              position: 'absolute',
              right: 0,
              top: 0,
              border: 0,
              paddingLeft: 0,
            },
            body: {
              padding: 0,
            },
            heading: {
              [theme.fn.smallerThan('sm')]: {},
            },
          })}
          onClose={() => setShowModal(null)}
        >
          <Stack
            sx={(theme) => ({
              padding: rem(16),
              gap: rem(16),
              [theme.fn.largerThan('sm')]: {
                padding: rem(32),
                gap: rem(32),
              },
            })}
          >
            <Stack spacing={8} sx={{ textAlign: 'center' }}>
              <Heading tag="h4">
                {f({
                  id: 'doctorCard.confirmCityPopup.heading',
                })}
              </Heading>
              <Text size="p2">
                {f(
                  {
                    id: 'doctorCard.confirmCityPopup.content',
                  },
                  {
                    location: <strong>{showModal?.cityName}</strong>,
                  },
                )}
              </Text>
            </Stack>
            <Flex
              gap={12}
              sx={{
                button: {
                  flex: 1,
                },
              }}
            >
              <Button
                variant="tertiary"
                onClick={() => {
                  setShowModal(null)
                  onCancel?.()
                }}
              >
                {f({ id: 'carePathDoctorList.confirmLocModal.cancel' })}
              </Button>
              <Button
                onClick={() => {
                  if (showModal?.url) window.open(showModal?.url)
                  setShowModal(null)
                }}
              >
                {f({ id: 'confirm' })}
              </Button>
            </Flex>
          </Stack>
        </Modal>
      )}
    </>
  )
}

export { DoctorCard }
